//region ENUM AutoOrderAllStatusTypesEnum
/**
 * ### AutoOrderAllStatusTypesEnum
 * Enum representing all possible status types for an auto order.
 *
 * @enum {string}
 * @description
 * This enum consolidates every status type that an auto order can have in the system,
 * serving as the single source of truth for status references in other enums.
 * It covers creation, processing, scheduling, email, PDF, IQ statuses, and more.
 *
 * @example
 * const status: AutoOrderAllStatusTypesEnum = AutoOrderAllStatusTypesEnum.CREATED;
 */
export enum AutoOrderAllStatusTypesEnum {
  CREATED = 'CREATED',
  DELETING = 'DELETING',
  EMPTY = 'EMPTY',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  PREPARED = 'PREPARED',
  READY = 'READY',
  SCANNED = 'SCANNED',
  SCHEDULED = 'SCHEDULED',
  SENT = 'SENT',
  SKIP = 'SKIP',
  SUBMITTED = 'SUBMITTED',
  SUCCESS = 'SUCCESS',
  UNSCHEDULED = 'UNSCHEDULED',
}
//endregion

//region ENUM AutoOrderAllStatusTypesEnumHumanReadable
/**
 * ### AutoOrderAllStatusTypesEnumHumanReadable
 * Human-readable labels corresponding to all auto order status types.
 *
 * @enum {string}
 * @description
 * Provides user-friendly strings for display purposes,
 * mapping directly to values from AutoOrderAllStatusTypesEnum.
 *
 * @example
 * const label: AutoOrderAllStatusTypesEnumHumanReadable = AutoOrderAllStatusTypesEnumHumanReadable.CREATED;
 */
export enum AutoOrderAllStatusTypesEnumHumanReadable {
  CREATED = 'Created',
  DELETING = 'Deleting',
  EMPTY = 'Empty',
  FAILED = 'Failed',
  PENDING = 'Pending',
  PREPARED = 'Prepared',
  READY = 'Ready',
  SCANNED = 'Scanned',
  SCHEDULED = 'Scheduled',
  SENT = 'Sent',
  SKIP = 'Skipped',
  SUBMITTED = 'Submitted',
  SUCCESS = 'Success',
  UNSCHEDULED = 'Unscheduled',
}
//endregion

//region Type AutoOrderAllStatusTypesType
/**
 * ### AutoOrderAllStatusTypesType
 * Type representing all keys from the AutoOrderAllStatusTypesEnum.
 *
 * @typedef {keyof typeof AutoOrderAllStatusTypesEnum} AutoOrderAllStatusTypesType
 *
 * @example
 * const statusKey: AutoOrderAllStatusTypesType = 'CREATED';
 */
export type AutoOrderAllStatusTypesType = keyof typeof AutoOrderAllStatusTypesEnum;
//endregion



//region ENUM AutoOrderStatusTypeEnum
/**
 * ### AutoOrderStatusTypeEnum
 * Enum representing main auto order status types.
 *
 * @enum {string}
 * @description
 * Core lifecycle statuses for auto orders, referencing the master AllStatusTypes enum.
 *
 * @example
 * const status: AutoOrderStatusTypeEnum = AutoOrderStatusTypeEnum.SUBMITTED;
 */
export enum AutoOrderStatusTypeEnum {
  CREATED = AutoOrderAllStatusTypesEnum.CREATED,
  EMPTY = AutoOrderAllStatusTypesEnum.EMPTY,
  READY = AutoOrderAllStatusTypesEnum.READY,
  PREPARED = AutoOrderAllStatusTypesEnum.PREPARED,
  SUBMITTED = AutoOrderAllStatusTypesEnum.SUBMITTED,
  SUCCESS = AutoOrderAllStatusTypesEnum.SUCCESS,
  FAILED = AutoOrderAllStatusTypesEnum.FAILED,
  DELETING = AutoOrderAllStatusTypesEnum.DELETING,
}
//endregion

//region ENUM AutoOrderStatusTypeEnumHumanReadable
/**
 * ### AutoOrderStatusTypeEnumHumanReadable
 * Human-readable labels for main auto order statuses.
 *
 * @enum {string}
 * @description
 * Display names corresponding to AutoOrderStatusTypeEnum values.
 *
 * @example
 * const label: AutoOrderStatusTypeEnumHumanReadable = AutoOrderStatusTypeEnumHumanReadable.PREPARED;
 */
export enum AutoOrderStatusTypeEnumHumanReadable {
  CREATED = 'Created',
  EMPTY = 'Empty',
  READY = 'Ready',
  PREPARED = 'Prepared',
  SUBMITTED = 'Submitted',
  SUCCESS = 'Success',
  FAILED = 'Failed',
  DELETING = 'Deleting',
}
//endregion

//region Type AutoOrderStatusType
/**
 * ### AutoOrderStatusType
 * Type representing keys of AutoOrderStatusTypeEnum.
 *
 * @typedef {keyof typeof AutoOrderStatusTypeEnum} AutoOrderStatusType
 *
 * @example
 * const statusKey: AutoOrderStatusType = 'FAILED';
 */
export type AutoOrderStatusType = keyof typeof AutoOrderStatusTypeEnum;
//endregion



//region ENUM AutoOrderScheduledTypeEnum
/**
 * ### AutoOrderScheduledTypeEnum
 * Enum representing status types specifically related to auto order scheduling.
 *
 * @enum {string}
 * @description
 * Includes the subset of statuses from AutoOrderAllStatusTypesEnum that deal with scheduling phases.
 *
 * @example
 * const scheduleStatus: AutoOrderScheduledTypeEnum = AutoOrderScheduledTypeEnum.SCHEDULED;
 */
export enum AutoOrderScheduledTypeEnum {
  CREATED = AutoOrderAllStatusTypesEnum.CREATED,
  SCHEDULED = AutoOrderAllStatusTypesEnum.SCHEDULED,
  SCANNED = AutoOrderAllStatusTypesEnum.SCANNED,
  UNSCHEDULED = AutoOrderAllStatusTypesEnum.UNSCHEDULED,
}
//endregion

//region ENUM AutoOrderScheduledTypeEnumHumanReadable
/**
 * ### AutoOrderScheduledTypeEnumHumanReadable
 * Human-readable labels for auto order scheduled statuses.
 *
 * @enum {string}
 * @description
 * Provides display-friendly strings matching AutoOrderScheduledTypeEnum values.
 *
 * @example
 * const label: AutoOrderScheduledTypeEnumHumanReadable = AutoOrderScheduledTypeEnumHumanReadable.SCHEDULED;
 */
export enum AutoOrderScheduledTypeEnumHumanReadable {
  CREATED = 'Created',
  SCHEDULED = 'Scheduled',
  SCANNED = 'Scanned',
  UNSCHEDULED = 'Unscheduled',
}
//endregion

//region Type AutoOrderScheduledType
/**
 * ### AutoOrderScheduledType
 * Type representing keys of AutoOrderScheduledTypeEnum.
 *
 * @typedef {keyof typeof AutoOrderScheduledTypeEnum} AutoOrderScheduledType
 *
 * @example
 * const scheduledKey: AutoOrderScheduledType = 'SCANNED';
 */
export type AutoOrderScheduledType = keyof typeof AutoOrderScheduledTypeEnum;
//endregion



//region ENUM AutoOrderEmailStatusTypeEnum
/**
 * ### AutoOrderEmailStatusTypeEnum
 * Enum representing status types for auto order email processing.
 *
 * @enum {string}
 * @description
 * Includes statuses relevant to email workflow stages within auto orders.
 *
 * @example
 * const emailStatus: AutoOrderEmailStatusTypeEnum = AutoOrderEmailStatusTypeEnum.SENT;
 */
export enum AutoOrderEmailStatusTypeEnum {
  READY = AutoOrderAllStatusTypesEnum.READY,
  PENDING = AutoOrderAllStatusTypesEnum.PENDING,
  SKIP = AutoOrderAllStatusTypesEnum.SKIP,
  SENT = AutoOrderAllStatusTypesEnum.SENT,
  FAILED = AutoOrderAllStatusTypesEnum.FAILED,
}
//endregion

//region ENUM AutoOrderEmailStatusTypeEnumHumanReadable
/**
 * ### AutoOrderEmailStatusTypeEnumHumanReadable
 * Human-readable labels for auto order email statuses.
 *
 * @enum {string}
 * @description
 * Display-friendly string equivalents of AutoOrderEmailStatusTypeEnum.
 *
 * @example
 * const label: AutoOrderEmailStatusTypeEnumHumanReadable = AutoOrderEmailStatusTypeEnumHumanReadable.SKIP;
 */
export enum AutoOrderEmailStatusTypeEnumHumanReadable {
  READY = 'Ready',
  PENDING = 'Pending',
  SKIP = 'Skipped',
  SENT = 'Sent',
  FAILED = 'Failed',
}
//endregion

//region Type AutoOrderEmailStatusType
/**
 * ### AutoOrderEmailStatusType
 * Type representing keys of AutoOrderEmailStatusTypeEnum.
 *
 * @typedef {keyof typeof AutoOrderEmailStatusTypeEnum} AutoOrderEmailStatusType
 *
 * @example
 * const emailStatusKey: AutoOrderEmailStatusType = 'FAILED';
 */
export type AutoOrderEmailStatusType = keyof typeof AutoOrderEmailStatusTypeEnum;
//endregion



//region ENUM AutoOrderIQStatusTypeEnum
/**
 * ### AutoOrderIQStatusTypeEnum
 * Enum representing internal queue (IQ) status types for auto orders.
 *
 * @enum {string}
 * @description
 * Status types related to the internal queue processing of auto orders.
 *
 * @example
 * const iqStatus: AutoOrderIQStatusTypeEnum = AutoOrderIQStatusTypeEnum.SUCCESS;
 */
export enum AutoOrderIQStatusTypeEnum {
  READY = AutoOrderAllStatusTypesEnum.READY,
  SUCCESS = AutoOrderAllStatusTypesEnum.SUCCESS,
  FAILED = AutoOrderAllStatusTypesEnum.FAILED,
}
//endregion

//region ENUM AutoOrderIQStatusTypeEnumHumanReadable
/**
 * ### AutoOrderIQStatusTypeEnumHumanReadable
 * Human-readable labels for internal queue status types.
 *
 * @enum {string}
 * @description
 * Display-friendly strings matching AutoOrderIQStatusTypeEnum values.
 *
 * @example
 * const label: AutoOrderIQStatusTypeEnumHumanReadable = AutoOrderIQStatusTypeEnumHumanReadable.READY;
 */
export enum AutoOrderIQStatusTypeEnumHumanReadable {
  READY = 'Ready',
  SUCCESS = 'Success',
  FAILED = 'Failed',
}
//endregion

//region Type AutoOrderIQStatusType
/**
 * ### AutoOrderIQStatusType
 * Type representing keys of AutoOrderIQStatusTypeEnum.
 *
 * @typedef {keyof typeof AutoOrderIQStatusTypeEnum} AutoOrderIQStatusType
 *
 * @example
 * const iqStatusKey: AutoOrderIQStatusType = 'FAILED';
 */
export type AutoOrderIQStatusType = keyof typeof AutoOrderIQStatusTypeEnum;
//endregion



//region ENUM AutoOrderPDFStatusTypeEnum
/**
 * ### AutoOrderPDFStatusTypeEnum
 * Enum representing PDF generation statuses for auto orders.
 *
 * @enum {string}
 * @description
 * Status types related to PDF processing in the auto order lifecycle.
 *
 * @example
 * const pdfStatus: AutoOrderPDFStatusTypeEnum = AutoOrderPDFStatusTypeEnum.SUCCESS;
 */
export enum AutoOrderPDFStatusTypeEnum {
  READY = AutoOrderAllStatusTypesEnum.READY,
  SUCCESS = AutoOrderAllStatusTypesEnum.SUCCESS,
  FAILED = AutoOrderAllStatusTypesEnum.FAILED,
}
//endregion

//region ENUM AutoOrderPDFStatusTypeEnumHumanReadable
/**
 * ### AutoOrderPDFStatusTypeEnumHumanReadable
 * Human-readable labels for PDF status types.
 *
 * @enum {string}
 * @description
 * User-facing string labels corresponding to PDF status enums.
 *
 * @example
 * const label: AutoOrderPDFStatusTypeEnumHumanReadable = AutoOrderPDFStatusTypeEnumHumanReadable.FAILED;
 */
export enum AutoOrderPDFStatusTypeEnumHumanReadable {
  READY = 'Ready',
  SUCCESS = 'Success',
  FAILED = 'Failed',
}
//endregion

//region Type AutoOrderPDFStatusType
/**
 * ### AutoOrderPDFStatusType
 * Type representing keys of AutoOrderPDFStatusTypeEnum.
 *
 * @typedef {keyof typeof AutoOrderPDFStatusTypeEnum} AutoOrderPDFStatusType
 *
 * @example
 * const pdfStatusKey: AutoOrderPDFStatusType = 'READY';
 */
export type AutoOrderPDFStatusType = keyof typeof AutoOrderPDFStatusTypeEnum;
//endregion
